'use client'

import type {
  FoldoutColumn,
  NavigationFoldoutProps,
} from '@maestro/commerce-blocks/components/navigation-header/navigation-foldout.types'

import { useCallback, useMemo } from 'react'

import { useMobileNavigation } from '@maestro/commerce-blocks/providers/mobile-navigation/context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer'

export const FOLDOUT_MAX_LEVELS = 3

export const useNavigationFoldout = ({ menu }: NavigationFoldoutProps) => {
  const { activeNodeIds, openLevelAt, closeLevel, closeMenu, isOpen } =
    useMobileNavigation()
  const { publish } = useEventBusContext()

  const columns = useMemo<FoldoutColumn[]>(() => {
    if (!menu) {
      return []
    }

    const cols: FoldoutColumn[] = [{ key: menu.id, items: menu.children ?? [] }]

    let currentItems = menu.children ?? []
    for (
      let level = 1;
      level < activeNodeIds.length && level < FOLDOUT_MAX_LEVELS;
      level++
    ) {
      const parent = currentItems.find(
        (item) => item._key === activeNodeIds[level]
      )
      if (!parent?.children?.length) {
        break
      }
      cols.push({ key: parent._key, parent, items: parent.children })
      currentItems = parent.children
    }

    return cols
  }, [menu, activeNodeIds])

  const isExpandable = useCallback(
    (columnIndex: number, hasChildren?: boolean) =>
      Boolean(hasChildren) && columnIndex < FOLDOUT_MAX_LEVELS - 1,
    []
  )

  const openColumn = useCallback(
    (columnIndex: number, id: string) => {
      openLevelAt(columnIndex, id)
    },
    [openLevelAt]
  )

  const handleNavigate = useCallback(
    (columnIndex: number, clickedTitle?: string) => {
      const ancestorTitles = columns
        .slice(1, columnIndex + 1)
        .map((column) => column.parent?.title)
      const levels = [...ancestorTitles, clickedTitle]

      publish(EventType.MENU_CLICKED, {
        level1: levels[0] || undefined,
        level2: levels[1] || undefined,
        level3: levels[2] || undefined,
      })

      closeMenu()
    },
    [columns, publish, closeMenu]
  )

  return {
    columns,
    activeNodeIds,
    closeLevel,
    closeMenu,
    handleNavigate,
    isExpandable,
    isOpen,
    openColumn,
  }
}
